import './Chat.css';
import '../Modal/Modal.css';

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    getUserContacts, createUserContact, markUserContactRead,
    getDirectMessages, createDirectMessage, updateDirectMessage, deleteDirectMessage,
    getGroupChats, createGroupChat, updateGroupChat, deleteGroupChat,
    getGroupChatMembers, createGroupChatMember, updateGroupChatMember, markGroupChatMemberRead, deleteGroupChatMember,
    getGroupChatMessages, createGroupChatMessage, updateGroupChatMessage, deleteGroupChatMessage,
    getHasUnreadMessages, connectChatSocket,
} from '../../api/communication';
import { getAllUsers, getCurrentUser, updateUser } from '../../api/users_institutions';
import { playNotificationSound } from '../../utils/notificationSound';
import LoadingIndicator from '../Common/LoadingIndicator';

const sortBySentAt = (a, b) => new Date(a.sent_at) - new Date(b.sent_at);

/**
 * Reply arrow. Stroke-only and sized in CSS (.msg-actions svg) so it inherits the button's
 * currentColor and hover opacity like the rest of the action row.
 *
 * `flipped` mirrors it horizontally. .msg-actions sits on the opposite side of the bubble
 * depending on who sent the message (.chat-bubble-row is row-reverse for 'sent'), so mirroring
 * keeps the arrow pointing back AT the message being replied to in both cases -- unflipped it
 * would point away from your own messages, out into empty space.
 */
const ReplyIcon = ({ flipped = false }) => (
    <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        style={flipped ? { transform: 'scaleX(-1)' } : undefined}
    >
        <polyline points="9 17 4 12 9 7" />
        <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
    </svg>
);

// Person glyph -- distinguishes "reply privately to this person" from the group-wide ReplyIcon
// above without needing to combine two shapes into one small icon.
const ReplyPrivatelyIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
        <circle cx="12" cy="7" r="4" />
    </svg>
);

const EditIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
);

const DeleteIcon = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
);

// Widget resize (desktop only -- see isTablet/isMobile gating where this is used).
const MIN_WIDGET_WIDTH = 525;
const MIN_WIDGET_HEIGHT = 380;
const DEFAULT_WIDGET_SIZE = { width: 650, height: 500 };
const WIDGET_SIZE_STORAGE_KEY = 'chatWidgetSize';
// Must match .chat-widget's own `right`/`bottom` in ChatDraft1.css -- the widget grows toward the
// top-left from those fixed edges, so without subtracting them here the opposite (left/top)
// edge would land past the viewport well before width/height actually reached its full size.
const WIDGET_RIGHT_OFFSET = 30;
const WIDGET_BOTTOM_OFFSET = 90;

const clampWidgetSize = (size) => ({
    width: Math.min(Math.max(size.width, MIN_WIDGET_WIDTH), window.innerWidth - WIDGET_RIGHT_OFFSET),
    height: Math.min(Math.max(size.height, MIN_WIDGET_HEIGHT), window.innerHeight - WIDGET_BOTTOM_OFFSET),
});

// Reads the size saved by a previous session (see the persistence effect below), re-clamped
// against *this* load's viewport -- a size saved on a wide monitor shouldn't overflow a
// narrower one it's opened on later.
const readStoredWidgetSize = () => {
    if (typeof window === 'undefined') return DEFAULT_WIDGET_SIZE;
    try {
        const raw = window.localStorage.getItem(WIDGET_SIZE_STORAGE_KEY);
        if (!raw) return DEFAULT_WIDGET_SIZE;
        const parsed = JSON.parse(raw);
        if (typeof parsed?.width !== 'number' || typeof parsed?.height !== 'number') return DEFAULT_WIDGET_SIZE;
        return clampWidgetSize(parsed);
    } catch {
        return DEFAULT_WIDGET_SIZE;
    }
};

const persistWidgetSize = (size) => {
    try {
        window.localStorage.setItem(WIDGET_SIZE_STORAGE_KEY, JSON.stringify(size));
    } catch {
        // Storage can be unavailable (private mode, quota) -- the size just won't be
        // remembered next time, which isn't worth surfacing to the user.
    }
};

// showToggleButton: hosts that provide their own way in (Exam/Test place a 💬 button under their
// editor controls) pass false, so the floating "Support Chat" pill doesn't double up with it.
function Chat({ isOpen = false, onToggle, showToggleButton = true }) {
    const { t, i18n } = useTranslation();
    const currentUser = getCurrentUser();
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 760px)').matches
    );

    // Mobile Screen detected
    useEffect(() => {
        const mql = window.matchMedia('(max-width: 760px)');
        const handleChange = (e) => {
        setIsMobile(e.matches);
        };
        mql.addEventListener('change', handleChange);
        return () => mql.removeEventListener('change', handleChange);
    }, []);

    // Tablet screen detected -- same breakpoint Navbar.jsx uses. Resizing (below) is a
    // desktop-only affordance: on mobile the widget is a full-screen takeover and on tablet it's
    // still cramped enough that free-form resize would just get in the way.
    const [isTablet, setIsTablet] = useState(
        () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1024px)').matches
    );
    useEffect(() => {
        const mql = window.matchMedia('(max-width: 1024px)');
        const handleChange = (e) => setIsTablet(e.matches);
        mql.addEventListener('change', handleChange);
        return () => mql.removeEventListener('change', handleChange);
    }, []);
    const canResizeWidget = !isMobile;

    // Drag-to-resize from the widget's top-left corner. The widget is positioned via
    // bottom/right (see .chat-widget), so growing width/height naturally expands toward the
    // top-left -- exactly the direction dragging that corner outward implies. Persisted to
    // localStorage (see readStoredWidgetSize/persistWidgetSize) so it's remembered next time.
    const [widgetSize, setWidgetSize] = useState(readStoredWidgetSize);
    const resizeStateRef = useRef(null);

    const handleResizeMouseMove = (e) => {
        const state = resizeStateRef.current;
        if (!state) return;
        const dx = state.startX - e.clientX;
        const dy = state.startY - e.clientY;
        // Read fresh on every move rather than caching at drag-start, so the cap always tracks
        // the actual browser page size (e.g. the window itself being resized mid-drag).
        const next = clampWidgetSize({ width: state.startWidth + dx, height: state.startHeight + dy });
        // Tracked on the ref (always current) rather than read back from `widgetSize` state in
        // handleResizeMouseUp -- that closure is captured once at mousedown and would otherwise
        // see whatever `widgetSize` was *before* the drag, not the final dragged-to size.
        state.lastSize = next;
        setWidgetSize(next);
    };

    const handleResizeMouseUp = () => {
        const state = resizeStateRef.current;
        if (state?.lastSize) persistWidgetSize(state.lastSize);
        resizeStateRef.current = null;
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
    };

    const handleResizeMouseDown = (e) => {
        if (!canResizeWidget) return;
        e.preventDefault();
        resizeStateRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            startWidth: widgetSize.width,
            startHeight: widgetSize.height,
            lastSize: null,
        };
        document.body.style.userSelect = 'none';
        document.addEventListener('mousemove', handleResizeMouseMove);
        document.addEventListener('mouseup', handleResizeMouseUp);
    };

    // Safety net: release the drag listeners if the widget unmounts mid-drag.
    useEffect(() => () => {
        document.removeEventListener('mousemove', handleResizeMouseMove);
        document.removeEventListener('mouseup', handleResizeMouseUp);
        document.body.style.userSelect = '';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keeps a manually-resized widget from ever sitting outside the browser page: if the window
    // itself shrinks (not just a drag on the handle), re-clamp and persist the correction so a
    // reload doesn't bring back the now-too-large size.
    useEffect(() => {
        const handleWindowResize = () => {
            setWidgetSize((prev) => {
                const next = clampWidgetSize(prev);
                if (next.width === prev.width && next.height === prev.height) return prev;
                persistWidgetSize(next);
                return next;
            });
        };
        window.addEventListener('resize', handleWindowResize);
        return () => window.removeEventListener('resize', handleWindowResize);
    }, []);

    // Mobile-only: which pane the single-column layout is showing. Irrelevant on desktop (both
    // panes render side by side there regardless of this value). Every fresh open starts back on
    // the conversation list, same as first opening the widget.
    const [mobileView, setMobileView] = useState('sidebar'); // 'sidebar' | 'main'

    // Chat-toggle unread badge (mobile Navbar.jsx + desktop button here share the
    // ".chat-notif-badge" class, so both are driven from one place) -- a distinct class from
    // Notification.jsx's ".bell-notif-badge" (the two only share their visual CSS rule, not a
    // toggle-able class), so toggling one can never affect the other.
    const setChatBadgeVisible = (visible) => {
        document.querySelectorAll('.chat-notif-badge').forEach((el) => {
            el.style.display = visible ? 'flex' : 'none';
        });
    };

    // Whether *anything* is unread, for the badge -- backed by UserContact.unread_message and
    // GroupChatMember.unread_message (see api/models.py), one flag per conversation rather than a
    // single global flag, so the sidebar can also highlight which conversation it is (see
    // `conversations` below). Starts from a one-off existence check on mount (hasUnreadFromApi);
    // once contacts/groupMembers actually load (hasLoadedOnce), they become the live source of
    // truth instead, kept in sync by the same events/actions that already update those lists.
    const [hasUnreadFromApi, setHasUnreadFromApi] = useState(false);
    useEffect(() => {
        if (!currentUser?.id) return;
        getHasUnreadMessages().then(setHasUnreadFromApi).catch((error) => {
            console.error('Error checking unread messages:', error);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // The websocket handler below is set up once per currentUser/hasLoadedOnce change (see its
    // own effect's dependency array) and would otherwise close over a stale `isOpen` from
    // whichever render that last happened on -- a ref keeps it reading the live value on every
    // event without needing to tear down and reconnect the socket on every open/close.
    const isOpenRef = useRef(isOpen);
    useEffect(() => {
        isOpenRef.current = isOpen;
        if (isOpen) setMobileView('sidebar');
    }, [isOpen]);

    // Persists server-side (User.new_chat_message, see api/models.py) so the badge stays
    // cleared for this account across devices/sessions, not just this tab's DOM -- same pattern
    // as Navbar.jsx's toggleNotifications clearing new_announcement/new_notification on open.
    // The visible badge itself is still driven by the live hasUnreadFromApi/conversations state
    // above, not this flag -- this is just a parallel per-user record of "has unread chat".
    useEffect(() => {
        if (!isOpen || !currentUser?.id) return;
        updateUser(currentUser.id, { new_chat_message: false }).catch((error) => {
            console.error('Error clearing chat notification flag:', error);
        });
    }, [isOpen, currentUser?.id]);

    const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

    const [adminUsers, setAdminUsers] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [directMessages, setDirectMessages] = useState([]);
    const [groupChats, setGroupChats] = useState([]);
    const [groupMembers, setGroupMembers] = useState([]);
    const [groupMessages, setGroupMessages] = useState([]);

    const [activeConversation, setActiveConversation] = useState(null); // { type: 'dm'|'group', counterpartId|groupChatId, myRowId }
    const [messageDraft, setMessageDraft] = useState('');
    const [editingMessage, setEditingMessage] = useState(null); // { id, text }
    const messageInputRef = useRef(null);
    // { file, url, name, type: 'image'|'video'|'file' } for the attachment queued on the composer,
    // or null. `url` is an object URL for the preview bar/thumbnail only -- it's never sent.
    const [pendingAttachment, setPendingAttachment] = useState(null);
    // The message the composer is currently replying to, or null.
    // { id, senderName, text, mediaUrl } -- a flat snapshot rather than the message object, so the
    // preview bar renders the same way for a DM (sender implied by the UserContact row) and a
    // group message (explicit sender), and stays correct if the quoted row scrolls out of view.
    const [replyTarget, setReplyTarget] = useState(null);
    const [isSendingMessage, setIsSendingMessage] = useState(false);

    // Grows a message textarea with its content, up to the CSS max-height (where overflow-y:
    // auto takes over) -- called on mount (via ref callback) and on every change. scrollHeight
    // never includes the border (only padding), but box-sizing: border-box means the height we
    // set here does -- without adding it back, the box ends up ~2px short of its own content,
    // so the scrollbar appears well before max-height is actually reached.
    const autoResizeTextarea = (el) => {
        if (!el) return;
        el.style.height = 'auto';
        const borderHeight = el.offsetHeight - el.clientHeight;
        el.style.height = `${el.scrollHeight + borderHeight}px`;
    };

    const [isNewChatOpen, setIsNewChatOpen] = useState(false);
    const [newChatMode, setNewChatMode] = useState('dm');
    const [newChatSearch, setNewChatSearch] = useState('');
    const [selectedDmAdminId, setSelectedDmAdminId] = useState(null);
    const [newGroupTitle, setNewGroupTitle] = useState('');
    const [newGroupDesc, setNewGroupDesc] = useState('');
    const [newGroupPhoto, setNewGroupPhoto] = useState(null);
    const [selectedGroupMemberIds, setSelectedGroupMemberIds] = useState([]);
    const [isSubmittingNewChat, setIsSubmittingNewChat] = useState(false);

    const [isAddPersonOpen, setIsAddPersonOpen] = useState(false);
    const [addPersonSearch, setAddPersonSearch] = useState('');

    const [isGroupInfoOpen, setIsGroupInfoOpen] = useState(false);
    const [groupInfoTitleDraft, setGroupInfoTitleDraft] = useState('');
    const [groupInfoDescDraft, setGroupInfoDescDraft] = useState('');
    // undefined = photo untouched this session, null = marked for removal, File = new upload
    const [groupInfoPhotoFile, setGroupInfoPhotoFile] = useState(undefined);
    const [groupInfoPhotoPreview, setGroupInfoPhotoPreview] = useState('');

    // The backend only returns a plain media_url, not a content-type flag, so the attachment
    // preview/bubble kind is inferred from the file extension -- same signal the composer's
    // pendingAttachment.type uses (via the File's MIME type) before upload.
    const getAttachmentKind = (url) => {
        if (!url) return null;
        const clean = url.split('?')[0].toLowerCase();
        if (/\.(png|jpe?g|gif|webp|svg)$/.test(clean)) return 'image';
        if (/\.(mp4|webm|mov|ogv?)$/.test(clean)) return 'video';
        return 'file';
    };

    const renderMessageAttachment = (mediaUrl) => {
        if (!mediaUrl) return null;
        const kind = getAttachmentKind(mediaUrl);
        if (kind === 'image') {
            return <img src={mediaUrl} alt="" className="msg-image" onClick={() => window.open(mediaUrl, '_blank', 'noopener')} />;
        }
        if (kind === 'video') {
            return (
                <div className="msg-video-container" onClick={() => window.open(mediaUrl, '_blank', 'noopener')}>
                    <video src={mediaUrl} />
                    <div className="play-overlay">▶</div>
                </div>
            );
        }
        return (
            <a
                href={mediaUrl} target="_blank" rel="noopener noreferrer"
                style={{
                    display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(0,0,0,0.1)',
                    padding: '8px 12px', borderRadius: '8px', marginBottom: '8px', color: 'inherit', textDecoration: 'none',
                }}
            >
                <span>📎</span><span style={{ fontSize: '0.8rem' }}>{t('chat.attachment')}</span>
            </a>
        );
    };

    const formatMessageTime = (value) => {
        if (!value) return '';
        return new Intl.DateTimeFormat(i18n.language, {
            month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true,
        }).format(new Date(value));
    };

    const loadChatData = async () => {
        try {
            const [users, contactRows, dmRows, groupRows, memberRows, groupMsgRows] = await Promise.all([
                getAllUsers(), getUserContacts(), getDirectMessages(),
                getGroupChats(), getGroupChatMembers(), getGroupChatMessages(),
            ]);
            setAdminUsers((Array.isArray(users) ? users : []).filter((u) => u.is_staff && u.id !== currentUser?.id));
            setContacts(Array.isArray(contactRows) ? contactRows : []);
            setDirectMessages(Array.isArray(dmRows) ? dmRows : []);
            setGroupChats(Array.isArray(groupRows) ? groupRows : []);
            setGroupMembers(Array.isArray(memberRows) ? memberRows : []);
            setGroupMessages(Array.isArray(groupMsgRows) ? groupMsgRows : []);
        } catch (error) {
            console.error('Error loading chat data:', error);
        }
    };

    // Loaded once, the first time the widget is opened -- not on every mount of Navbar, since
    // Chat is always mounted (so the WebSocket below can deliver a notification badge even while
    // the widget itself is closed) but there's no point fetching chat history nobody's looking at.
    useEffect(() => {
        if (isOpen && !hasLoadedOnce) {
            setHasLoadedOnce(true);
            loadChatData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Real-time updates: each event just identifies what changed (see api/ws_utils.py), so the
    // handler refetches the same REST lists the initial load used rather than trusting a
    // separately-shaped push payload to stay in sync with the database.
    useEffect(() => {
        if (!currentUser?.id) return;

        const handleEvent = (event) => {
            // Runs even before the widget's first open (hasLoadedOnce), matching the comment
            // above this effect -- otherwise a message that arrives before the user has ever
            // opened the widget this session would never light the badge. Once contacts/
            // groupMembers actually load below, the derived hasUnreadFromState takes over as the
            // authoritative value (see its useMemo) -- this is only a stopgap before that.
            if ((event.event === 'direct_message' || event.event === 'group_message') && !isOpenRef.current) {
                setHasUnreadFromApi(true);
                // The widget being closed here also rules out this being an echo of a message
                // the current user themselves just sent -- sending requires the widget open.
                playNotificationSound();
            }

            if (!hasLoadedOnce) return;
            switch (event.event) {
                case 'contact_added':
                    getUserContacts().then(setContacts).catch((error) => console.error('Error refreshing contacts:', error));
                    break;
                case 'direct_message':
                    getDirectMessages().then(setDirectMessages).catch((error) => console.error('Error refreshing messages:', error));
                    // Also refreshes unread_message flags for the sidebar highlight/badge.
                    getUserContacts().then(setContacts).catch((error) => console.error('Error refreshing contacts:', error));
                    break;
                case 'group_message':
                    getGroupChatMessages().then(setGroupMessages).catch((error) => console.error('Error refreshing group messages:', error));
                    // Also refreshes unread_message flags for the sidebar highlight/badge.
                    getGroupChatMembers().then(setGroupMembers).catch((error) => console.error('Error refreshing group members:', error));
                    break;
                case 'group_member_added':
                case 'group_member_updated':
                case 'group_member_removed':
                    Promise.all([getGroupChatMembers(), getGroupChats()])
                        .then(([memberRows, groupRows]) => { setGroupMembers(memberRows); setGroupChats(groupRows); })
                        .catch((error) => console.error('Error refreshing groups:', error));
                    break;
                case 'group_updated':
                    getGroupChats().then(setGroupChats).catch((error) => console.error('Error refreshing groups:', error));
                    break;
                case 'group_deleted':
                    Promise.all([getGroupChatMembers(), getGroupChats()])
                        .then(([memberRows, groupRows]) => {
                            setGroupMembers(memberRows);
                            setGroupChats(groupRows);
                            setActiveConversation((current) => (
                                current?.type === 'group' && current.groupChatId === event.group_chat ? null : current
                            ));
                        })
                        .catch((error) => console.error('Error refreshing groups:', error));
                    break;
                default:
                    break;
            }
        };

        const closeSocket = connectChatSocket(handleEvent);
        return closeSocket;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.id, hasLoadedOnce]);

    const myContactRows = useMemo(
        () => contacts.filter((c) => c.user === currentUser?.id),
        [contacts, currentUser?.id]
    );

    const dmRowIdsFor = (counterpartId) => {
        const mine = contacts.find((c) => c.user === currentUser?.id && c.contact === counterpartId);
        const theirs = contacts.find((c) => c.user === counterpartId && c.contact === currentUser?.id);
        return [mine?.id, theirs?.id].filter((id) => id !== undefined);
    };

    const conversations = useMemo(() => {
        const dmThreads = myContactRows.map((row) => {
            const rowIds = dmRowIdsFor(row.contact);
            const msgs = directMessages.filter((m) => rowIds.includes(m.user_contact)).sort(sortBySentAt);
            const last = msgs[msgs.length - 1];
            const name = [row.contact_first_name, row.contact_last_name].filter(Boolean).join(' ') || row.contact_username;
            const counterpart = adminUsers.find((u) => u.id === row.contact);
            return {
                key: `dm-${row.contact}`, type: 'dm', counterpartId: row.contact, myRowId: row.id,
                name, photoUrl: counterpart?.profile_picture_url || null,
                lastMessage: last?.message || null, lastMessageAt: last?.sent_at || null,
                hasUnread: !!row.unread_message,
            };
        });

        const myMemberships = groupMembers.filter((m) => m.user === currentUser?.id);
        const groupThreads = myMemberships.map((membership) => {
            const group = groupChats.find((g) => g.id === membership.group_chat);
            const msgs = groupMessages.filter((m) => m.group_chat === membership.group_chat).sort(sortBySentAt);
            const last = msgs[msgs.length - 1];
            return {
                key: `group-${membership.group_chat}`, type: 'group', groupChatId: membership.group_chat,
                name: group?.group_title || 'Group Chat', photoUrl: group?.group_photo_url,
                lastMessage: last?.message || null, lastMessageAt: last?.sent_at || null,
                hasUnread: !!membership.unread_message, myMembershipId: membership.id,
            };
        });

        return [...dmThreads, ...groupThreads].sort(
            (a, b) => new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0)
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [myContactRows, contacts, directMessages, groupChats, groupMembers, groupMessages, adminUsers, currentUser?.id]);

    // Once contacts/groupMembers actually load, they're the live, per-conversation source of
    // truth for "is anything unread" (kept correct by every action/event that already updates
    // those lists) -- hasUnreadFromApi only covers the gap before that first load (see its effect
    // above).
    const hasUnreadFromState = useMemo(
        () => conversations.some((c) => c.hasUnread),
        [conversations]
    );
    const hasUnread = hasLoadedOnce ? hasUnreadFromState : hasUnreadFromApi;
    useEffect(() => {
        setChatBadgeVisible(hasUnread);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasUnread]);

    const activeMessages = useMemo(() => {
        if (!activeConversation) return [];
        if (activeConversation.type === 'dm') {
            const rowIds = dmRowIdsFor(activeConversation.counterpartId);
            return directMessages.filter((m) => rowIds.includes(m.user_contact)).sort(sortBySentAt);
        }
        return groupMessages.filter((m) => m.group_chat === activeConversation.groupChatId).sort(sortBySentAt);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeConversation, directMessages, groupMessages, contacts]);

    const activeGroupMembers = useMemo(() => {
        if (!activeConversation || activeConversation.type !== 'group') return [];
        return groupMembers.filter((m) => m.group_chat === activeConversation.groupChatId);
    }, [activeConversation, groupMembers]);

    const isActiveGroupAdmin = activeGroupMembers.some((m) => m.user === currentUser?.id && m.is_admin);

    const toggleWidget = () => onToggle?.();

    const selectConversation = (conversation) => {
        clearAttachment();
        setActiveConversation(conversation.type === 'dm'
            ? { type: 'dm', counterpartId: conversation.counterpartId, myRowId: conversation.myRowId }
            : { type: 'group', groupChatId: conversation.groupChatId });
        setMobileView('main');

        if (!conversation.hasUnread) return;
        if (conversation.type === 'dm') {
            markUserContactRead(conversation.myRowId)
                .then((updated) => setContacts((prev) => prev.map((c) => (c.id === updated.id ? updated : c))))
                .catch((error) => console.error('Error marking conversation as read:', error));
        } else {
            markGroupChatMemberRead(conversation.myMembershipId)
                .then((updated) => setGroupMembers((prev) => prev.map((m) => (m.id === updated.id ? updated : m))))
                .catch((error) => console.error('Error marking group as read:', error));
        }
    };

    const backToConversationList = () => setMobileView('sidebar');

    const revokeAttachmentUrl = (attachment) => {
        if (attachment?.url) URL.revokeObjectURL(attachment.url);
    };

    const handleAttachmentChange = (e) => {
        const file = e.target.files?.[0];
        e.target.value = ''; // reset so picking the same file again still fires onChange
        if (!file) return;
        let type = 'file';
        if (file.type.startsWith('image/')) type = 'image';
        else if (file.type.startsWith('video/')) type = 'video';
        revokeAttachmentUrl(pendingAttachment);
        setPendingAttachment({ file, url: URL.createObjectURL(file), name: file.name, type });
    };

    const clearAttachment = () => {
        revokeAttachmentUrl(pendingAttachment);
        setPendingAttachment(null);
    };

    // `senderName` is resolved by the caller: a DirectMessage has no sender field (it is implied
    // by the UserContact row -- see the model), so only the render site knows who sent it.
    const handleReplyToMessage = (message, senderName) => {
        setReplyTarget({
            id: message.id,
            senderName,
            text: message.message || '',
            mediaUrl: message.media_url || null,
        });
        messageInputRef.current?.focus();
    };

    const cancelReply = () => setReplyTarget(null);

    // A reply id only means anything inside the conversation it came from -- the backend rejects
    // a cross-conversation reply_to outright, so drop it rather than send a doomed request.
    // handleReplyPrivately below deliberately switches conversation *and* sets a fresh
    // (id-less) replyTarget in the same gesture -- skipNextReplyClearRef lets that one survive
    // instead of being wiped out by this effect a moment later.
    const skipNextReplyClearRef = useRef(false);
    useEffect(() => {
        if (skipNextReplyClearRef.current) {
            skipNextReplyClearRef.current = false;
            return;
        }
        setReplyTarget(null);
    }, [activeConversation?.counterpartId, activeConversation?.groupChatId, activeConversation?.type]);

    // Starts (or reuses) a private DM with the group message's sender and pre-fills the composer
    // with a quote of what they said. There's no backend reply_to across conversations (see
    // above), so this is a client-side quoting aid only -- replyTarget.id stays null, and
    // handleSendMessage knows not to attach a reply_to when it's missing.
    const handleReplyPrivately = async (message, senderName) => {
        if (!message?.user || message.user === currentUser?.id) return;
        try {
            let myRow = contacts.find((c) => c.user === currentUser.id && c.contact === message.user);
            if (!myRow) {
                myRow = await createUserContact({ contact: message.user });
                setContacts((prev) => [...prev, myRow]);
            }
            skipNextReplyClearRef.current = true;
            clearAttachment();
            setActiveConversation({ type: 'dm', counterpartId: message.user, myRowId: myRow.id });
            setMobileView('main');
            setReplyTarget({
                id: null,
                senderName,
                text: message.message || '',
                mediaUrl: message.media_url || null,
            });
            messageInputRef.current?.focus();
        } catch (error) {
            console.error('Error starting private reply:', error);
        }
    };

    const handleSendMessage = async () => {
        const text = messageDraft.trim();
        const attachment = pendingAttachment;
        const replyingTo = replyTarget;
        if ((!text && !attachment) || !activeConversation || isSendingMessage) return;

        setIsSendingMessage(true);
        setMessageDraft('');
        setPendingAttachment(null);
        setReplyTarget(null);
        if (messageInputRef.current) messageInputRef.current.style.height = 'auto';
        try {
            const payload = { message: text };
            if (attachment) payload.media = attachment.file;
            // Omitted entirely rather than sent as null: with an attachment the body is FormData,
            // where a null would be serialised as the string "null".
            if (replyingTo?.id != null) {
                payload.reply_to = replyingTo.id;
            } else if (replyingTo) {
                // A private reply (see handleReplyPrivately) quotes a GroupChatMessage, which
                // reply_to can never point to (it's a same-model self-FK on each side -- see
                // DirectMessage.reply_to in models.py) -- quoted_* snapshots the content as plain
                // fields instead, so DirectMessageSerializer can still surface it through the
                // usual reply_to_sender_name/_message/_media_url for both sender and recipient.
                payload.quoted_sender_name = replyingTo.senderName;
                payload.quoted_message = replyingTo.text;
                if (replyingTo.mediaUrl) payload.quoted_media_url = replyingTo.mediaUrl;
            }
            if (activeConversation.type === 'dm') {
                const created = await createDirectMessage({ user_contact: activeConversation.myRowId, ...payload });
                setDirectMessages((prev) => [...prev, created]);
            } else {
                const created = await createGroupChatMessage({ group_chat: activeConversation.groupChatId, ...payload });
                setGroupMessages((prev) => [...prev, created]);
            }
            revokeAttachmentUrl(attachment);
        } catch (error) {
            console.error('Error sending message:', error);
            setMessageDraft(text);
            setPendingAttachment(attachment);
            setReplyTarget(replyingTo);
        } finally {
            setIsSendingMessage(false);
        }
    };

    const handleDeleteMessage = async (message) => {
        if (!window.confirm(t('chat.confirmDeleteMessage'))) return;
        try {
            if (activeConversation.type === 'dm') {
                await deleteDirectMessage(message.id);
                setDirectMessages((prev) => prev.filter((m) => m.id !== message.id));
            } else {
                await deleteGroupChatMessage(message.id);
                setGroupMessages((prev) => prev.filter((m) => m.id !== message.id));
            }
        } catch (error) {
            console.error('Error deleting message:', error);
        }
    };

    const handleEditMessage = (message) => {
        setEditingMessage({ id: message.id, text: message.message });
    };

    const cancelEditMessage = () => setEditingMessage(null);

    const submitEditMessage = async () => {
        const text = editingMessage?.text.trim();
        if (!text) return;
        try {
            if (activeConversation.type === 'dm') {
                const updated = await updateDirectMessage(editingMessage.id, { message: text });
                setDirectMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
            } else {
                const updated = await updateGroupChatMessage(editingMessage.id, { message: text });
                setGroupMessages((prev) => prev.map((m) => (m.id === updated.id ? updated : m)));
            }
            setEditingMessage(null);
        } catch (error) {
            console.error('Error editing message:', error);
        }
    };

    const openNewChatModal = () => {
        setNewChatMode('dm');
        setNewChatSearch('');
        setSelectedDmAdminId(null);
        setNewGroupTitle('');
        setNewGroupDesc('');
        setNewGroupPhoto(null);
        setSelectedGroupMemberIds([]);
        setIsNewChatOpen(true);
    };

    const toggleGroupMemberSelection = (userId) => {
        setSelectedGroupMemberIds((current) =>
            current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]
        );
    };

    const filteredAdminUsers = (query) => {
        const q = query.toLowerCase().trim();
        if (!q) return adminUsers;
        return adminUsers.filter((admin) =>
            admin.username.toLowerCase().includes(q) ||
            `${admin.first_name} ${admin.last_name}`.toLowerCase().includes(q)
        );
    };

    const adminDisplayName = (admin) =>
        [admin.first_name, admin.last_name].filter(Boolean).join(' ') || admin.username;

    // Resolves a user id to full user info (email, profile_picture_url, ...) -- currentUser is
    // excluded from adminUsers, so it's checked separately.
    const getUserInfoById = (userId) => {
        if (userId === currentUser?.id) return currentUser;
        return adminUsers.find((u) => u.id === userId) || null;
    };

    const getMemberUserInfo = (member) => getUserInfoById(member.user);

    const handleStartNewChat = async () => {
        if (isSubmittingNewChat) return;
        setIsSubmittingNewChat(true);
        try {
            if (newChatMode === 'dm') {
                if (!selectedDmAdminId) {
                    alert(t('chat.selectAdminToMessage'));
                    return;
                }
                let myRow = contacts.find((c) => c.user === currentUser.id && c.contact === selectedDmAdminId);
                if (!myRow) {
                    myRow = await createUserContact({ contact: selectedDmAdminId });
                    setContacts((prev) => [...prev, myRow]);
                }
                setIsNewChatOpen(false);
                setActiveConversation({ type: 'dm', counterpartId: selectedDmAdminId, myRowId: myRow.id });
            } else {
                if (!newGroupTitle.trim()) {
                    alert(t('chat.groupTitleRequired'));
                    return;
                }
                const groupData = { group_title: newGroupTitle.trim(), description: newGroupDesc.trim() };
                if (newGroupPhoto) groupData.group_photo = newGroupPhoto;
                const newGroup = await createGroupChat(groupData);
                setGroupChats((prev) => [...prev, newGroup]);
                setGroupMembers((prev) => [...prev, { group_chat: newGroup.id, user: currentUser.id, user_username: currentUser.username }]);

                await Promise.all(selectedGroupMemberIds.map(async (userId) => {
                    const member = await createGroupChatMember({ group_chat: newGroup.id, user: userId, is_admin: false });
                    setGroupMembers((prev) => [...prev, member]);
                }));

                setIsNewChatOpen(false);
                setActiveConversation({ type: 'group', groupChatId: newGroup.id });
            }
        } catch (error) {
            console.error('Error starting new chat:', error);
            alert(t('chat.startChatFailed'));
        } finally {
            setIsSubmittingNewChat(false);
        }
    };

    const openAddPersonModal = () => {
        setAddPersonSearch('');
        setIsAddPersonOpen(true);
    };

    const handleAddPerson = async (userId) => {
        if (!activeConversation || activeConversation.type !== 'group') return;
        try {
            const member = await createGroupChatMember({ group_chat: activeConversation.groupChatId, user: userId, is_admin: false });
            setGroupMembers((prev) => [...prev, member]);
        } catch (error) {
            console.error('Error adding member:', error);
        }
    };

    const handleRemoveMember = async (member) => {
        if (!window.confirm(t('chat.confirmRemoveMember'))) return;
        try {
            await deleteGroupChatMember(member.id);
            setGroupMembers((prev) => prev.filter((m) => m.id !== member.id));
        } catch (error) {
            console.error('Error removing member:', error);
        }
    };

    const handleToggleMemberAdmin = async (member) => {
        try {
            const updated = await updateGroupChatMember(member.id, !member.is_admin);
            setGroupMembers((prev) => prev.map((m) => (m.id === member.id ? updated : m)));
        } catch (error) {
            console.error('Error updating member admin status:', error);
        }
    };

    const openGroupInfoModal = () => {
        if (!activeConversation || activeConversation.type !== 'group') return;
        const group = groupChats.find((g) => g.id === activeConversation.groupChatId);
        setGroupInfoTitleDraft(group?.group_title || '');
        setGroupInfoDescDraft(group?.description || '');
        setGroupInfoPhotoFile(undefined);
        setGroupInfoPhotoPreview(group?.group_photo_url || '');
        setIsGroupInfoOpen(true);
    };

    const handleGroupInfoPhotoChange = (input) => {
        const file = input?.target?.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => setGroupInfoPhotoPreview(e.target?.result || '');
        reader.readAsDataURL(file);
        setGroupInfoPhotoFile(file);
        input.target.value = '';
    };

    const handleGroupInfoPhotoRemove = () => {
        setGroupInfoPhotoPreview('');
        setGroupInfoPhotoFile(null);
    };

    // Mirrors isSubmittingNewChat: the group-title/description/photo PATCH is the one primary
    // action in this modal, so it gets the same click-guard and the same in-flight indicator.
    const [isSavingGroupInfo, setIsSavingGroupInfo] = useState(false);
    const handleSaveGroupInfo = async () => {
        if (!activeConversation || activeConversation.type !== 'group' || isSavingGroupInfo) return;
        setIsSavingGroupInfo(true);
        try {
            const payload = {
                group_title: groupInfoTitleDraft.trim(),
                description: groupInfoDescDraft.trim(),
            };
            if (groupInfoPhotoFile instanceof File) payload.group_photo = groupInfoPhotoFile;
            else if (groupInfoPhotoFile === null) payload.group_photo = null;

            const updated = await updateGroupChat(activeConversation.groupChatId, payload);
            setGroupChats((prev) => prev.map((g) => (g.id === updated.id ? updated : g)));
            setGroupInfoPhotoFile(undefined);
            setGroupInfoPhotoPreview(updated.group_photo_url || '');
        } catch (error) {
            console.error('Error updating group chat:', error);
        } finally {
            setIsSavingGroupInfo(false);
        }
    };

    const handleDeleteGroupChat = async () => {
        if (!activeConversation || activeConversation.type !== 'group') return;
        if (!window.confirm(t('chat.confirmDeleteGroup'))) return;
        const groupChatId = activeConversation.groupChatId;
        try {
            await deleteGroupChat(groupChatId);
            setGroupChats((prev) => prev.filter((g) => g.id !== groupChatId));
            setGroupMembers((prev) => prev.filter((m) => m.group_chat !== groupChatId));
            setIsGroupInfoOpen(false);
            setActiveConversation(null);
        } catch (error) {
            console.error('Error deleting group chat:', error);
            alert(t('chat.deleteGroupFailed'));
        }
    };

    const activeConversationName = useMemo(() => {
        if (!activeConversation) return '';
        const found = conversations.find((c) =>
            activeConversation.type === 'dm' ? (c.type === 'dm' && c.counterpartId === activeConversation.counterpartId)
                : (c.type === 'group' && c.groupChatId === activeConversation.groupChatId)
        );
        return found?.name || (activeConversation.type === 'group' ? t('chat.groupChat') : '');
    }, [activeConversation, conversations, t]);

    const activeConversationPhotoUrl = useMemo(() => {
        if (!activeConversation) return null;
        const found = conversations.find((c) =>
            activeConversation.type === 'dm' ? (c.type === 'dm' && c.counterpartId === activeConversation.counterpartId)
                : (c.type === 'group' && c.groupChatId === activeConversation.groupChatId)
        );
        return found?.photoUrl || null;
    }, [activeConversation, conversations]);

    const renderAvatar = (name, photoUrl) => (
        photoUrl
            ? <img src={photoUrl} alt={name || ''} className="chat-pfp" style={{ objectFit: 'cover' }} />
            : <div className="chat-pfp">{(name || '?').charAt(0).toUpperCase()}</div>
    );

    return (
        <>
            {currentUser && !isMobile && showToggleButton && (
                <button className="chat-toggle-btn" onClick={toggleWidget}>
                    <svg
                        viewBox="0 0 24 24"
                        width="20"
                        height="20"
                        aria-hidden="true"
                        focusable="false"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M20 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-4.1-1.05L3 20l1.05-4.4A8.4 8.4 0 0 1 3 11.5a8.5 8.5 0 1 1 17 0Z" />
                        <path d="M8 11.5h.01M12 11.5h.01M16 11.5h.01" />
                    </svg>
                    {/*Chat Bubble*/} 
                    {t('chat.supportChat')}<span className="chat-notif-badge" style={{ display: 'none' }}>!</span>
                </button>
            )}

            <div
                id="chatWidget"
                className={`chat-widget chat-d1${isOpen ? ' show' : ''}${isMobile && mobileView === 'main' ? ' mobile-view-main' : ''}`}
                style={canResizeWidget ? { width: `${widgetSize.width}px`, height: `${widgetSize.height}px` } : undefined}
            >
                {canResizeWidget && (
                    <div className="chat-resize-handle" title={t('chat.resizeWidget')} onMouseDown={handleResizeMouseDown}>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                            <path d="M21 3 3 21M21 10 10 21M21 17l-4 4" />
                        </svg>
                    </div>
                )}
                <div className="chat-sidebar">
                    <div className="chat-sidebar-header">
                        <h4>{t('chat.messages')}</h4>
                        <button className="chat-new-btn" onClick={openNewChatModal}>＋ {t('chat.new')}</button>
                    </div>
                    <div className="chat-list">
                        {conversations.length === 0 ? (
                            <div style={{ padding: '20px', color: 'var(--text-muted)', textAlign: 'center', fontSize: '0.85rem' }}>
                                {t('chat.noActiveChats')}
                            </div>
                        ) : conversations.map((conversation) => {
                            const isActive = activeConversation && (
                                activeConversation.type === 'dm' ? (conversation.type === 'dm' && conversation.counterpartId === activeConversation.counterpartId)
                                    : (conversation.type === 'group' && conversation.groupChatId === activeConversation.groupChatId)
                            );
                            return (
                                <div
                                    key={conversation.key}
                                    className={`chat-list-item${isActive ? ' active' : ''}${conversation.hasUnread ? ' unread' : ''}`}
                                    onClick={() => selectConversation(conversation)}
                                >
                                    {renderAvatar(conversation.name, conversation.photoUrl)}
                                    <div className="chat-preview">
                                        <span className="chat-name">{conversation.name}</span>
                                        <span className="chat-msg-preview">{conversation.lastMessage || t('chat.noMessagesYet')}</span>
                                    </div>
                                    {conversation.hasUnread && <span className="chat-unread-dot" />}
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="chat-main">
                    {!activeConversation ? (
                        <>
                            <div className="chat-header">
                                <span className="chat-active-name">{t('chat.selectChat')}</span>
                                <button className="chat-close-btn" onClick={toggleWidget}>✕</button>
                            </div>
                            <div className="chat-empty-state">
                                <span style={{ fontSize: '3rem', marginBottom: '10px' }}>
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="35"
                                        height="35"
                                        aria-hidden="true"
                                        focusable="false"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <path d="M20 11.5a8.5 8.5 0 0 1-8.5 8.5 8.4 8.4 0 0 1-4.1-1.05L3 20l1.05-4.4A8.4 8.4 0 0 1 3 11.5a8.5 8.5 0 1 1 17 0Z" />
                                        <path d="M8 11.5h.01M12 11.5h.01M16 11.5h.01" />
                                    </svg>
                                </span>
                                <p style={{ margin: 0 }}>{t('chat.selectOrStartChat')}</p>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="chat-header">
                                {isMobile && (
                                    <button className="chat-back-btn" title={t('common.back')} onClick={backToConversationList}>←</button>
                                )}
                                <div className="chat-header-info" onClick={() => activeConversation.type === 'group' && openGroupInfoModal()}>
                                    {renderAvatar(activeConversationName, activeConversationPhotoUrl)}
                                    <span className="chat-active-name" title={activeConversationName}>{activeConversationName}</span>
                                </div>
                                <div className="chat-header-actions">
                                    {activeConversation.type === 'group' && isActiveGroupAdmin && (
                                        <button className="chat-add-person-btn" title={t('chat.manageMembers')} onClick={openAddPersonModal}>👤+</button>
                                    )}
                                    <button className="chat-close-btn" onClick={toggleWidget}>✕</button>
                                </div>
                            </div>
                            <div className="chat-history">
                                {activeMessages.length === 0 ? (
                                    <div className="chat-empty-state">
                                        <p style={{ margin: 0 }}>{t('chat.noMessagesYet')}</p>
                                    </div>
                                ) : activeMessages.map((message) => {
                                    const isMe = activeConversation.type === 'dm'
                                        ? message.user_contact === activeConversation.myRowId
                                        : message.user === currentUser.id;
                                    const senderName = activeConversation.type === 'group'
                                        ? ([message.user_first_name, message.user_last_name].filter(Boolean).join(' ') || message.user_username)
                                        : '';
                                    const messagePhotoUrl = isMe
                                        ? getUserInfoById(currentUser.id)?.profile_picture_url
                                        : (activeConversation.type === 'group' ? getUserInfoById(message.user)?.profile_picture_url : activeConversationPhotoUrl);
                                    return (
                                        <div key={message.id} className={`chat-message-container ${isMe ? 'sent' : 'received'}`}>
                                            <div className="chat-message-inner">
                                                {renderAvatar(isMe ? currentUser.username : (senderName || activeConversationName), messagePhotoUrl)}
                                                <div className="chat-bubble-col">
                                                    {!isMe && senderName && <span className="msg-sender-name">{senderName}</span>}
                                                    {editingMessage?.id === message.id ? (
                                                        <div className="msg-edit-row">
                                                            <textarea
                                                                ref={autoResizeTextarea}
                                                                className="msg-edit-input"
                                                                rows={1}
                                                                value={editingMessage.text}
                                                                autoFocus
                                                                onChange={(e) => { setEditingMessage((cur) => ({ ...cur, text: e.target.value })); autoResizeTextarea(e.target); }}
                                                                onKeyDown={(e) => {
                                                                    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitEditMessage(); }
                                                                    if (e.key === 'Escape') cancelEditMessage();
                                                                }}
                                                            />
                                                            <div className="msg-edit-actions">
                                                                <button onClick={submitEditMessage} title={t('common.save')}>✓</button>
                                                                <button onClick={cancelEditMessage} title={t('common.cancel')}>✕</button>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        <div className="chat-bubble-row">
                                                            <div className="msg-bubble">
                                                                {/* reply_to_sender_name covers both a real reply (reply_to is
                                                                    SET_NULL on delete, so a quoted message that's since been
                                                                    deleted simply stops quoting) and a private reply's quoted_*
                                                                    snapshot (see DirectMessageSerializer) -- either way, a
                                                                    sender name means there's something to show here. */}
                                                                {message.reply_to_sender_name && (
                                                                    <div className="msg-quote">
                                                                        <span className="msg-quote-sender">{message.reply_to_sender_name}</span>
                                                                        <span className="msg-quote-text">
                                                                            {message.reply_to_message
                                                                                || (message.reply_to_media_url ? `📎 ${t('chat.attachment')}` : '')}
                                                                        </span>
                                                                    </div>
                                                                )}
                                                                {renderMessageAttachment(message.media_url)}
                                                                {message.message}
                                                            </div>
                                                            <div className="msg-actions">
                                                                <button
                                                                    className="msg-action-icon"
                                                                    onClick={() => handleReplyToMessage(
                                                                        message,
                                                                        isMe ? t('chat.you') : (senderName || activeConversationName)
                                                                    )}
                                                                    title={t('chat.reply')}
                                                                >
                                                                    <ReplyIcon flipped={isMe} />
                                                                </button>
                                                                {activeConversation.type === 'group' && !isMe && (
                                                                    <button
                                                                        className="msg-action-icon"
                                                                        onClick={() => handleReplyPrivately(message, senderName || activeConversationName)}
                                                                        title={t('chat.replyPrivately')}
                                                                    >
                                                                        <ReplyPrivatelyIcon />
                                                                    </button>
                                                                )}
                                                                {isMe && (
                                                                    <>
                                                                        <button className="msg-action-icon" onClick={() => handleEditMessage(message)} title={t('common.edit')}>
                                                                            <EditIcon />
                                                                        </button>
                                                                        <button className="msg-action-icon" onClick={() => handleDeleteMessage(message)} title={t('common.delete')}>
                                                                            <DeleteIcon />
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )}
                                                    <span className="msg-time">{formatMessageTime(message.sent_at)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {replyTarget && (
                                <div className="chat-reply-preview">
                                    {/* The accent bar doubles as the "this is a quote" cue, the same
                                        convention used inside the bubble (.msg-quote). */}
                                    <span className="chat-reply-bar" aria-hidden="true" />
                                    <div className="chat-reply-body">
                                        <span className="chat-reply-title">
                                            {t('chat.replyingTo', { name: replyTarget.senderName })}
                                        </span>
                                        <span className="chat-reply-text">
                                            {replyTarget.text || (replyTarget.mediaUrl ? `📎 ${t('chat.attachment')}` : '')}
                                        </span>
                                    </div>
                                    {replyTarget.mediaUrl && getAttachmentKind(replyTarget.mediaUrl) === 'image' && (
                                        <img src={replyTarget.mediaUrl} alt="" className="chat-reply-thumb" />
                                    )}
                                    <button
                                        className="chat-reply-cancel"
                                        onClick={cancelReply}
                                        title={t('chat.cancelReply')}
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                            {pendingAttachment && (
                                <div className="chat-attachment-preview">
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                                        {pendingAttachment.type === 'image' ? (
                                            <img src={pendingAttachment.url} alt={pendingAttachment.name} style={{ height: '36px', borderRadius: '4px' }} />
                                        ) : pendingAttachment.type === 'video' ? (
                                            <span style={{ background: 'black', color: 'white', borderRadius: '4px', padding: '6px 10px', fontSize: '0.7rem' }}>▶</span>
                                        ) : (
                                            <span style={{ fontSize: '1.2rem' }}>📎</span>
                                        )}
                                        <span style={{ fontSize: '0.85rem', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                            {pendingAttachment.name}
                                        </span>
                                    </div>
                                    <button
                                        onClick={clearAttachment}
                                        title={t('chat.remove')}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--del-color)', fontSize: '1.1rem' }}
                                    >
                                        ✕
                                    </button>
                                </div>
                            )}
                            <div className="chat-input-area">
                                <button
                                    id="chatAttachBtn"
                                    className="chat-attach-btn"
                                    title={t('chat.attachFile')}
                                    style={{ color: pendingAttachment ? 'var(--accent)' : 'var(--text-muted)' }}
                                    onClick={() => document.getElementById('chatAttachmentInput').click()}
                                >
                                    📎
                                </button>
                                <input
                                    type="file"
                                    id="chatAttachmentInput"
                                    accept="image/*,video/*"
                                    style={{ display: 'none' }}
                                    onChange={handleAttachmentChange}
                                />
                                <textarea
                                    ref={messageInputRef}
                                    className="chat-message-input"
                                    rows={1}
                                    value={messageDraft}
                                    placeholder={t('chat.typeMessagePlaceholder')}
                                    onChange={(e) => { setMessageDraft(e.target.value); autoResizeTextarea(e.target); }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); }
                                    }}
                                />
                                <button className="chat-send-btn" onClick={handleSendMessage} disabled={isSendingMessage}>➤</button>
                            </div>
                        </>
                    )}
                </div>
            </div>

            <LoadingIndicator active={isSubmittingNewChat} label={t('common.loading')} />
            {isNewChatOpen && (
                <div className="Modal" style={{ zIndex: 10000 }} onClick={() => setIsNewChatOpen(false)}>
                    <div className="modal-box" style={{ width: '480px' }} onClick={(e) => e.stopPropagation()}>
                        <h2>{t('chat.startNewChat')}</h2>

                        <div className="new-chat-tabs">
                            <button className={`access-btn${newChatMode === 'dm' ? ' active' : ''}`} onClick={() => setNewChatMode('dm')}>{t('chat.directMessage')}</button>
                            <button className={`access-btn${newChatMode === 'group' ? ' active' : ''}`} onClick={() => setNewChatMode('group')}>{t('chat.createGroup')}</button>
                        </div>

                        {newChatMode === 'dm' ? (
                            <div>
                                <label className="label-text">{t('chat.selectAdmin')}</label>
                                <input
                                    type="text"
                                    className="login-input"
                                    placeholder={t('chat.searchAdminsPlaceholder')}
                                    value={newChatSearch}
                                    onChange={(e) => setNewChatSearch(e.target.value)}
                                />
                                <div style={{ maxHeight: '240px', overflowY: 'auto', marginBottom: '18px' }}>
                                    {filteredAdminUsers(newChatSearch).length === 0 ? (
                                        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('chat.noAdminsFound')}</p>
                                    ) : filteredAdminUsers(newChatSearch).map((admin) => (
                                        <label key={admin.id} className="chat-user-label">
                                            <input
                                                type="radio"
                                                name="new_chat_dm"
                                                checked={selectedDmAdminId === admin.id}
                                                onChange={() => setSelectedDmAdminId(admin.id)}
                                            />
                                            {renderAvatar(adminDisplayName(admin))}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'baseline' }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{adminDisplayName(admin)}</span>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{admin.username}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        ) : (
                            <div>
                                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label className="label-text">{t('chat.groupTitle')}</label>
                                        <input type="text" className="login-input" value={newGroupTitle} onChange={(e) => setNewGroupTitle(e.target.value)} placeholder={t('chat.groupTitlePlaceholder')} />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label className="label-text">{t('chat.groupPhotoOptional')}</label>
                                        <input type="file" accept="image/*" className="login-input" onChange={(e) => setNewGroupPhoto(e.target.files?.[0] || null)} />
                                    </div>
                                </div>
                                <label className="label-text">{t('chat.description')}</label>
                                <textarea className="login-input" value={newGroupDesc} onChange={(e) => setNewGroupDesc(e.target.value)} placeholder={t('chat.descriptionOptionalPlaceholder')}></textarea>

                                <label className="label-text">{t('chat.selectMembers')}</label>
                                <input
                                    type="text"
                                    className="login-input"
                                    placeholder={t('chat.searchAdminsPlaceholder')}
                                    value={newChatSearch}
                                    onChange={(e) => setNewChatSearch(e.target.value)}
                                />
                                <div style={{ maxHeight: '240px', overflowY: 'auto', marginBottom: '18px' }}>
                                    {filteredAdminUsers(newChatSearch).length === 0 ? (
                                        <p style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t('chat.noAdminsFound')}</p>
                                    ) : filteredAdminUsers(newChatSearch).map((admin) => (
                                        <label key={admin.id} className="chat-user-label">
                                            <input
                                                type="checkbox"
                                                checked={selectedGroupMemberIds.includes(admin.id)}
                                                onChange={() => toggleGroupMemberSelection(admin.id)}
                                            />
                                            {renderAvatar(adminDisplayName(admin))}
                                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'baseline' }}>
                                                <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{adminDisplayName(admin)}</span>
                                                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{admin.username}</span>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="modal-buttons">
                            <button className="btn-primary" onClick={handleStartNewChat} disabled={isSubmittingNewChat}>{t('chat.startChat')}</button>
                            <button className="btn-primary btn-secondary" onClick={() => setIsNewChatOpen(false)}>{t('common.cancel')}</button>
                        </div>
                    </div>
                </div>
            )}

            {isAddPersonOpen && activeConversation?.type === 'group' && (
                <div className="Modal" style={{ zIndex: 10000 }} onClick={() => setIsAddPersonOpen(false)}>
                    <div className="modal-box" style={{ width: '480px' }} onClick={(e) => e.stopPropagation()}>
                        <h2>{t('chat.manageMembers')}</h2>
                        <label className="label-text">{t('chat.currentMembers')}</label>
                        <div style={{ marginBottom: '18px' }}>
                            {activeGroupMembers.map((member) => (
                                <div key={member.id} className="chat-user-label" style={{ justifyContent: 'space-between', cursor: 'default' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        {renderAvatar(member.user_username)}
                                        <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>
                                            {member.user_username}{member.user === currentUser.id ? ` (${t('chat.you')})` : ''}
                                            {member.is_admin && (
                                                <span style={{ marginLeft: '6px', fontSize: '0.7rem', color: 'var(--accent)', fontWeight: 700 }}>
                                                    ({t('chat.admin')})
                                                </span>
                                            )}
                                        </span>
                                    </div>
                                    {member.user !== currentUser.id && (
                                        <div style={{ display: 'flex', gap: '6px' }}>
                                            <button
                                                className="remove-member-btn"
                                                style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}
                                                onClick={() => handleToggleMemberAdmin(member)}
                                            >
                                                {member.is_admin ? t('chat.removeAdmin') : t('chat.makeAdmin')}
                                            </button>
                                            <button className="remove-member-btn" onClick={() => handleRemoveMember(member)}>{t('chat.remove')}</button>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>

                        <label className="label-text">{t('chat.addMembers')}</label>
                        <input
                            type="text"
                            className="login-input"
                            placeholder={t('chat.searchAdminsPlaceholder')}
                            value={addPersonSearch}
                            onChange={(e) => setAddPersonSearch(e.target.value)}
                        />
                        <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '18px' }}>
                            {filteredAdminUsers(addPersonSearch)
                                .filter((admin) => !activeGroupMembers.some((m) => m.user === admin.id))
                                .map((admin) => (
                                    <label key={admin.id} className="chat-user-label">
                                        <button type="button" className="btn-primary" style={{ padding: '4px 10px', fontSize: '0.75rem', width: '75px', maxWidth: '75px' }} onClick={() => handleAddPerson(admin.id)}>{t('chat.add')}</button>
                                        <span style={{ width: 'stretch', maxWidth: '35%' }}></span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            {renderAvatar(adminDisplayName(admin))}
                                            <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>{adminDisplayName(admin)}</span>
                                        </div>
                                    </label>
                                ))}
                        </div>

                        <div className="modal-buttons">
                            <button className="btn-primary btn-secondary" onClick={() => setIsAddPersonOpen(false)}>{t('common.close')}</button>
                        </div>
                    </div>
                </div>
            )}

            <LoadingIndicator active={isSavingGroupInfo} label={t('common.loading')} />
            {isGroupInfoOpen && activeConversation?.type === 'group' && (
                <div className="Modal" style={{ zIndex: 10000 }} onClick={() => setIsGroupInfoOpen(false)}>
                    <div className="modal-box modal-box-centered" onClick={(e) => e.stopPropagation()}>
                        <div className="group-photo-edit">
                            {(isActiveGroupAdmin ? groupInfoPhotoPreview : activeConversationPhotoUrl)
                                ? <img
                                    src={isActiveGroupAdmin ? groupInfoPhotoPreview : activeConversationPhotoUrl}
                                    alt={activeConversationName}
                                    className="chat-pfp chat-pfp-lg"
                                  />
                                : <div className="chat-pfp chat-pfp-lg">{(activeConversationName || '?').charAt(0).toUpperCase()}</div>}
                            {isActiveGroupAdmin && (
                                <div className="group-photo-edit-actions">
                                    <label className="group-photo-edit-btn">
                                        {t('chat.changePhoto')}
                                        <input type="file" accept="image/*" onChange={handleGroupInfoPhotoChange} style={{ display: 'none' }} />
                                    </label>
                                    {groupInfoPhotoPreview && (
                                        <button type="button" className="group-photo-edit-btn group-photo-edit-btn-remove" onClick={handleGroupInfoPhotoRemove}>
                                            {t('chat.removePhoto')}
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        {isActiveGroupAdmin ? (
                            <input
                                type="text"
                                className="login-input"
                                value={groupInfoTitleDraft}
                                onChange={(e) => setGroupInfoTitleDraft(e.target.value)}
                                placeholder={t('chat.groupTitlePlaceholder')}
                            />
                        ) : (
                            <h3 className="group-info-title" style={{
                                margin: 0,
                                fontFamily: 'Fraunces, serif',
                                fontSize: "1.8rem",
                                color: "var(--text-main)",
                                justifySelf: "center",
                            }}>{activeConversationName}</h3>
                        )}
                        {isActiveGroupAdmin ? (
                            <textarea
                                className="login-input"
                                value={groupInfoDescDraft}
                                onChange={(e) => setGroupInfoDescDraft(e.target.value)}
                                placeholder={t('chat.descriptionOptionalPlaceholder')}
                            ></textarea>
                        ) : (
                            <p style={{ 
                                margin: 0,
                                fontSize: "0.9rem",
                                color: "var(--text-muted)",
                                maxWidth: "90%",
                                lineHeight: 1.5, 
                                justifySelf: "center",
                            }}>
                                {groupChats.find((g) => g.id === activeConversation.groupChatId)?.description || ''}
                            </p>
                        )}
                        <div className="group-info-members">
                            <h4 className="group-info-members-header">
                                {t('chat.memberCount', { count: activeGroupMembers.length })}
                            </h4>
                            {activeGroupMembers.map((member) => {
                                const info = getMemberUserInfo(member);
                                const displayName = info ? adminDisplayName(info) : member.user_username;
                                const isSelf = member.user === currentUser.id;
                                return (
                                    <div key={member.id} className="group-info-member-row">
                                        {renderAvatar(displayName, info?.profile_picture_url)}
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span className="group-info-member-name">
                                                {displayName}
                                                {isSelf && <span className="group-info-member-tag you">{t('chat.you')}</span>}
                                                {member.is_admin && <span className="group-info-member-tag admin">{t('chat.admin')}</span>}
                                            </span>
                                            {info?.email && <span className="group-info-member-email">{info.email}</span>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                        {isActiveGroupAdmin && (
                            <div className="modal-buttons" style={{ justifyContent: 'flex-end' }}>
                                <button className="btn-primary" onClick={handleSaveGroupInfo} disabled={isSavingGroupInfo}>{t('common.change')}</button>
                                <button className="btn-primary btn-delete" style={{ background: 'var(--del-color)' }} onClick={handleDeleteGroupChat}>{t('chat.deleteGroup')}</button>
                            </div>
                        )}
                        <div className="modal-buttons" style={{ justifyContent: 'flex-end' }}>
                            <button className="btn-primary btn-secondary" onClick={() => setIsGroupInfoOpen(false)}>{t('common.close')}</button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default Chat;
